import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Button, Space, Divider, Tooltip } from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined,
  OrderedListOutlined, UnorderedListOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
} from '@ant-design/icons';

const ToolbarBtn = ({ active, onClick, title, children }) => (
  <Tooltip title={title}>
    <Button
      size="small"
      type={active ? 'primary' : 'default'}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{ minWidth: 28, padding: '0 6px' }}
    >
      {children}
    </Button>
  </Tooltip>
);

const RichTextEditor = ({ value, onChange, placeholder = 'Escribí el contenido aquí...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  if (!editor) return null;

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        background: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        padding: '6px 10px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'center',
      }}>
        <Space size={2} wrap>
          <ToolbarBtn
            title="Negrita"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldOutlined />
          </ToolbarBtn>
          <ToolbarBtn
            title="Cursiva"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicOutlined />
          </ToolbarBtn>
          <ToolbarBtn
            title="Subrayado"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineOutlined />
          </ToolbarBtn>

          <Divider type="vertical" style={{ height: 18, margin: '0 2px' }} />

          <ToolbarBtn
            title="Lista con viñetas"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <UnorderedListOutlined />
          </ToolbarBtn>
          <ToolbarBtn
            title="Lista numerada"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <OrderedListOutlined />
          </ToolbarBtn>

          <Divider type="vertical" style={{ height: 18, margin: '0 2px' }} />

          <ToolbarBtn
            title="Alinear izquierda"
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <AlignLeftOutlined />
          </ToolbarBtn>
          <ToolbarBtn
            title="Centrar"
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <AlignCenterOutlined />
          </ToolbarBtn>
          <ToolbarBtn
            title="Alinear derecha"
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <AlignRightOutlined />
          </ToolbarBtn>

          <Divider type="vertical" style={{ height: 18, margin: '0 2px' }} />

          {[1, 2, 3].map(level => (
            <ToolbarBtn
              key={level}
              title={`Título ${level}`}
              active={editor.isActive('heading', { level })}
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
            >
              <span style={{ fontSize: 11, fontWeight: 700 }}>H{level}</span>
            </ToolbarBtn>
          ))}
        </Space>
      </div>

      {/* Editor area */}
      <EditorContent
        editor={editor}
        style={{ minHeight: 160 }}
      />

      <style>{`
        .tiptap {
          padding: 12px 14px;
          min-height: 160px;
          outline: none;
          font-size: 14px;
          line-height: 1.6;
        }
        .tiptap p { margin: 0 0 8px 0; }
        .tiptap ul, .tiptap ol { padding-left: 20px; margin: 0 0 8px 0; }
        .tiptap h1 { font-size: 22px; font-weight: 700; margin: 0 0 8px 0; }
        .tiptap h2 { font-size: 18px; font-weight: 700; margin: 0 0 8px 0; }
        .tiptap h3 { font-size: 15px; font-weight: 600; margin: 0 0 8px 0; }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #bbb;
          pointer-events: none;
          float: left;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
